import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import posthog from 'posthog-js';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import Paywall from './components/Paywall';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import Applications from './pages/Applications';
import Settings from './pages/Settings';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Help from './pages/Help';
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import Fill from './pages/Fill';
import Packs from './pages/Packs';
import PublicPack from './pages/PublicPack';
import Extension from './pages/Extension';
import { RouteSeo } from './components/RouteSeo';
import axios from 'axios';
import './App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios interceptor — always attach the Bearer token from localStorage
// alongside cookies, so the app keeps working even if cross-site cookies are
// blocked or the session cookie expires before the localStorage token.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ============== THEME CONTEXT ==============
const ThemeContext = createContext(null);

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// ============== AUTH CONTEXT ==============
const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user from API using stored token
  const fetchUser = async () => {
    const token = localStorage.getItem('auth_token');
    
    // No token = not logged in
    if (!token) {
      localStorage.removeItem('user_data');
      setUser(null);
      setLoading(false);
      return null;
    }

    try {
      // ALWAYS validate with server, don't use cached data
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
      localStorage.setItem('user_data', JSON.stringify(response.data));
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Fetch user error:', error);
      // Token invalid - clear everything
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      setUser(null);
      setLoading(false);
      return null;
    }
  };

  // On mount, check for session_id in URL or fetch existing user
  useEffect(() => {
    const init = async () => {
      const hash = window.location.hash;
      
      // If we have a session_id, exchange it for a token
      if (hash && hash.includes('session_id=')) {
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');
        
        if (sessionId) {
          try {
            const response = await axios.post(`${API}/auth/session`, 
              { session_id: sessionId },
              { withCredentials: true }
            );
            
            // Store the token
            if (response.data.session_token) {
              localStorage.setItem('auth_token', response.data.session_token);
            }
            
            // Set user directly
            setUser(response.data);
            setLoading(false);

            // Identify user in PostHog
            if (response.data.user_id) {
              try {
                posthog.identify(response.data.user_id, {
                  email: response.data.email,
                  name: response.data.name,
                  subscription_tier: response.data.subscription_tier,
                });
                posthog.capture('user_signed_in');
              } catch (e) {}
            }
            
            // Clean URL
            window.history.replaceState(null, '', '/dashboard');
            return;
          } catch (error) {
            console.error('Session exchange error:', error);
          }
        }
      }
      
      // No session_id, try to fetch existing user
      await fetchUser();
    };

    init();
  }, []);

  const login = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    const token = localStorage.getItem('auth_token');
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
    } catch (e) {}
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setUser(null);
    try { posthog.reset(); } catch (e) {}
    window.location.href = '/';
  };

  const refreshUser = () => fetchUser();

  // Show loading screen while processing auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="font-mono text-lg mb-4">[LOADING...]</div>
          <p className="text-muted-foreground">Please wait...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ============== PROTECTED ROUTE ==============
function ProtectedRoute({ children, requirePayment = true }) {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (requirePayment) {
    return <Paywall>{children}</Paywall>;
  }

  return children;
}

// ============== APP ==============
function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <RouteSeo />
          <div className="App min-h-screen bg-background text-foreground">
            <Navbar />
            <main>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute requirePayment={false}><Dashboard /></ProtectedRoute>
                } />
                <Route path="/documents" element={
                  <ProtectedRoute requirePayment={false}><Documents /></ProtectedRoute>
                } />
                <Route path="/applications" element={
                  <ProtectedRoute requirePayment={false}><Applications /></ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute requirePayment={false}><Settings /></ProtectedRoute>
                } />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/help" element={<Help />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/admin" element={
                  <ProtectedRoute requirePayment={false}><Admin /></ProtectedRoute>
                } />
                <Route path="/fill" element={
                  <ProtectedRoute requirePayment={false}><Fill /></ProtectedRoute>
                } />
                <Route path="/upgrade" element={
                  <ProtectedRoute requirePayment={false}><Paywall /></ProtectedRoute>
                } />
                <Route path="/packs" element={
                  <ProtectedRoute requirePayment={false}><Packs /></ProtectedRoute>
                } />
                <Route path="/pack/:shareId" element={<PublicPack />} />
                <Route path="/extension" element={<Extension />} />
              </Routes>
            </main>
            <Toaster position="bottom-right" />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;